import { useState, useEffect, useMemo } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'
import { getUserActiveOrgId } from '@/lib/multiOrgHelpers'

interface OrgConfig {
  snapScanEnabled: boolean
  snapScanDirectieEnabled: boolean
  tempeltoolEnabled: boolean
  tempeltoolOrgEnabled: boolean
  lesbezoekenEnabled: boolean
  schoolleiderEvaluatieEnabled: boolean
  schoolleiderCollegialeVisitatieEnabled: boolean
  verslagenEnabled: boolean
  go2030Enabled: boolean
  videoObpEnabled: boolean
  homePageConfigEnabled: boolean
  dienstbrievenEnabled: boolean
  sopEnabled: boolean
  swpEnabled: boolean
  mijnABCEnabled: boolean
  mijnVakdossierEnabled: boolean
  selfAssessmentsEnabled: boolean
  ulkEnabled: boolean
  glpeEnabled: boolean
  glpeZichtbaarVoor: string[]
  leerlingenaantallenEnabled: boolean
  leerlingenstromenEnabled: boolean
  leerlingenherkomstEnabled: boolean
  regionaalAanbodEnabled: boolean
  avgRegisterEnabled: boolean
  bvhEnabled: boolean
  gevolgdeNascholingenTeamOverzichtEnabled: boolean
  goGebruikersrollenEnabled: boolean
}

const DEFAULT_CONFIG: OrgConfig = {
  snapScanEnabled: false,
  snapScanDirectieEnabled: false,
  tempeltoolEnabled: false,
  tempeltoolOrgEnabled: false,
  lesbezoekenEnabled: false,
  schoolleiderEvaluatieEnabled: false,
  schoolleiderCollegialeVisitatieEnabled: false,
  verslagenEnabled: false,
  go2030Enabled: false,
  videoObpEnabled: false,
  homePageConfigEnabled: false,
  dienstbrievenEnabled: false,
  sopEnabled: false,
  swpEnabled: false,
  mijnABCEnabled: false,
  mijnVakdossierEnabled: false,
  selfAssessmentsEnabled: false,
  ulkEnabled: false,
  glpeEnabled: false,
  glpeZichtbaarVoor: [],
  leerlingenaantallenEnabled: false,
  leerlingenstromenEnabled: false,
  leerlingenherkomstEnabled: false,
  regionaalAanbodEnabled: false,
  avgRegisterEnabled: false,
  bvhEnabled: false,
  gevolgdeNascholingenTeamOverzichtEnabled: false,
  goGebruikersrollenEnabled: false,
}

/**
 * Haalt org-specifieke feature config op en cached per orgId.
 * Combineert orgFeatures (platform-admin toggles) met school-level config.
 *
 * Twee-lagen model voor SnapScan, SnapScan directie, Tempeltool:
 * - orgFeatures.snapScan = platform-admin bepaalt of de org het KAN gebruiken
 * - snapScanConfig.enabled = school-admin zet het AAN/UIT
 * - Beide moeten true zijn voor de feature zichtbaar is
 */
export function useOrgConfig(): OrgConfig & { loading: boolean } {
  const { userProfile } = useAuthState()
  const activeOrgId = getUserActiveOrgId(userProfile)

  const [cache, setCache] = useState<Record<string, OrgConfig>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!activeOrgId) {
      setLoading(false)
      return
    }
    if (activeOrgId in cache) {
      setLoading(false)
      return
    }

    setLoading(true)

    // Haal orgFeatures, snapScanConfig en snapScanDirectieConfig parallel op
    const orgFeaturesPromise = getDoc(doc(db, 'orgFeatures', activeOrgId)).then(snap => {
      const data = snap.exists() ? snap.data() : {}
      return {
        lesbezoeken: data?.lesbezoeken === true,
        schoolleiderEvaluatie: data?.schoolleiderEvaluatie === true,
        schoolleiderCollegialeVisitatie: data?.schoolleiderCollegialeVisitatie === true,
        verslagen: data?.verslagen === true,
        go2030: data?.go2030 === true,
        // Backwards compatible: undefined = niet beperkt (true)
        // Alleen expliciet false blokkeert de feature
        snapScan: data?.snapScan !== false,
        snapScanDirectie: data?.snapScanDirectie !== false,
        tempeltool: data?.tempeltool !== false,
        videoObp: data?.videoObp === true,
        homePageConfig: data?.homePageConfig === true,
        dienstbrieven: data?.dienstbrieven === true,
        sop: data?.sop === true,
        swp: data?.swp === true,
        mijnABC: data?.mijnABC === true,
        mijnVakdossier: data?.mijnVakdossier === true,
        selfAssessments: data?.selfAssessments === true,
        ulk: data?.ulk === true,
        glpe: data?.glpe === true,
        glpeZichtbaarVoor: Array.isArray(data?.glpeZichtbaarVoor) ? data!.glpeZichtbaarVoor as string[] : [],
        leerlingenaantallen: data?.leerlingenaantallen === true,
        leerlingenstromen: data?.leerlingenstromen === true,
        leerlingenherkomst: data?.leerlingenherkomst === true,
        regionaalAanbod: data?.regionaalAanbod === true,
        gevolgdeNascholingenOverzicht: data?.gevolgdeNascholingenOverzicht === true,
        gevolgdeNascholingenTeamOverzicht: data?.gevolgdeNascholingenTeamOverzicht === true,
        avgRegister: data?.avgRegister === true,
        bvh: data?.bvh === true,
        goGebruikersrollen: data?.goGebruikersrollen === true,
      }
    }).catch(() => ({
      lesbezoeken: false, schoolleiderEvaluatie: false, schoolleiderCollegialeVisitatie: false, verslagen: false, go2030: false,
      snapScan: true, snapScanDirectie: true, tempeltool: true, videoObp: false,
      homePageConfig: false, dienstbrieven: false, sop: false, swp: false, mijnABC: false, mijnVakdossier: false, selfAssessments: false, ulk: false, glpe: false, glpeZichtbaarVoor: [] as string[], leerlingenaantallen: false, leerlingenstromen: false, leerlingenherkomst: false, regionaalAanbod: false, gevolgdeNascholingenOverzicht: false, gevolgdeNascholingenTeamOverzicht: false, avgRegister: false, bvh: false, goGebruikersrollen: false,
    }))

    const snapConfigPromise = getDoc(doc(db, 'snapScanConfig', activeOrgId)).then(snap => {
      const data = snap.exists() ? snap.data() : {}
      return {
        snapScanSchool: data?.enabled === true,
        tempeltoolSchool: data?.tempeltoolEnabled === true,
      }
    }).catch(() => ({ snapScanSchool: false, tempeltoolSchool: false }))

    const directieConfigPromise = getDoc(doc(db, 'snapScanDirectieConfig', activeOrgId)).then(snap => {
      const data = snap.exists() ? snap.data() : {}
      return { snapScanDirectieSchool: data?.enabled === true }
    }).catch(() => ({ snapScanDirectieSchool: false }))

    Promise.all([orgFeaturesPromise, snapConfigPromise, directieConfigPromise]).then(([orgFeats, snapCfg, directieCfg]) => {
      const config: OrgConfig = {
        // Twee-lagen: orgFeatures AND school-config moeten beide true zijn
        snapScanEnabled: orgFeats.snapScan && snapCfg.snapScanSchool,
        snapScanDirectieEnabled: orgFeats.snapScanDirectie && directieCfg.snapScanDirectieSchool,
        tempeltoolEnabled: orgFeats.tempeltool && snapCfg.tempeltoolSchool,
        tempeltoolOrgEnabled: orgFeats.tempeltool,
        // Eén-laag: alleen orgFeatures
        lesbezoekenEnabled: orgFeats.lesbezoeken,
        schoolleiderEvaluatieEnabled: orgFeats.schoolleiderEvaluatie,
        schoolleiderCollegialeVisitatieEnabled: orgFeats.schoolleiderCollegialeVisitatie,
        verslagenEnabled: orgFeats.verslagen,
        go2030Enabled: orgFeats.go2030,
        videoObpEnabled: orgFeats.videoObp,
        homePageConfigEnabled: orgFeats.homePageConfig,
        dienstbrievenEnabled: orgFeats.dienstbrieven,
        sopEnabled: orgFeats.sop,
        swpEnabled: orgFeats.swp,
        mijnABCEnabled: orgFeats.mijnABC,
        mijnVakdossierEnabled: orgFeats.mijnVakdossier,
        selfAssessmentsEnabled: orgFeats.selfAssessments,
        ulkEnabled: orgFeats.ulk,
        glpeEnabled: orgFeats.glpe,
        glpeZichtbaarVoor: (orgFeats as any).glpeZichtbaarVoor ?? [],
        leerlingenaantallenEnabled: orgFeats.leerlingenaantallen,
        leerlingenstromenEnabled: orgFeats.leerlingenstromen,
        leerlingenherkomstEnabled: orgFeats.leerlingenherkomst,
        regionaalAanbodEnabled: orgFeats.regionaalAanbod,
        avgRegisterEnabled: orgFeats.avgRegister,
        bvhEnabled: orgFeats.bvh,
        gevolgdeNascholingenTeamOverzichtEnabled: orgFeats.gevolgdeNascholingenTeamOverzicht,
        goGebruikersrollenEnabled: orgFeats.goGebruikersrollen,
      }

      setCache(prev => ({ ...prev, [activeOrgId]: config }))
      setLoading(false)
    })
  }, [activeOrgId])

  const config = useMemo(() => {
    if (!activeOrgId || !(activeOrgId in cache)) return DEFAULT_CONFIG
    return cache[activeOrgId]
  }, [activeOrgId, cache])

  return { ...config, loading }
}
